import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Checklist from "@/components/sections/Checklist";

interface ChecklistGroup {
  label?: string;
  items: string[];
}

interface EditorialServiceBlockProps {
  title: string;
  intro: string;
  description: string[];
  checklists?: ChecklistGroup[];
  nudge?: string;
  cta: { text: string; href: string };
  image?: string;
  imageAlt?: string;
  reverse?: boolean;
  variant?: "light" | "dark";
}

const EditorialServiceBlock = ({
  title,
  intro,
  description,
  checklists,
  nudge,
  cta,
  image,
  imageAlt = "",
  reverse = false,
  variant = "dark",
}: EditorialServiceBlockProps) => {
  const isDark = variant === "dark";
  const heading = isDark ? "text-secondary-foreground" : "text-foreground";
  const introColor = isDark ? "text-secondary-foreground/85" : "text-foreground/85";
  const body = isDark ? "text-secondary-foreground/70" : "text-muted-foreground";
  const label = isDark ? "text-secondary-foreground/80" : "text-foreground/80";
  const nudgeColor = isDark ? "text-secondary-foreground/55" : "text-muted-foreground/60";
  const checklistOverride = isDark
    ? "[&_li]:text-secondary-foreground/70 [&_svg]:text-accent"
    : "";

  const textBlock = (
    <div className="flex items-center">
      <div className="max-w-[620px] w-full py-20 md:py-28 pt-4">
        <h2 className={`font-headline text-3xl md:text-[2.75rem] leading-[1.1] tracking-tight ${heading}`}>
          {title}
        </h2>

        <p className={`mt-6 ${introColor} text-lg md:text-xl leading-relaxed font-medium`}>
          {intro}
        </p>

        <div className="mt-6 space-y-5">
          {description.map((paragraph, i) => (
            <p key={i} className={`${body} text-base md:text-lg leading-[1.75]`}>
              {paragraph}
            </p>
          ))}
        </div>

        {checklists?.map((group, i) => (
          <div key={i} className="mt-10">
            {group.label && (
              <p className={`${label} text-sm uppercase tracking-widest mb-5`}>
                {group.label}
              </p>
            )}
            <div className={`${checklistOverride} [&_li]:py-1.5`}>
              <Checklist items={group.items} />
            </div>
          </div>
        ))}

        {nudge && (
          <p className={`mt-10 ${nudgeColor} text-sm italic`}>{nudge}</p>
        )}

        <Link
          to={cta.href}
          className="group inline-flex items-center gap-2 mt-10 font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
        >
          {cta.text}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );

  const imageBlock = image ? (
    <div className="relative h-full min-h-[400px] md:min-h-[560px]">
      <img
        src={image}
        alt={imageAlt}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        width={1280}
        height={960}
      />
    </div>
  ) : null;

  return (
    <div className="relative w-screen left-1/2 -translate-x-1/2">
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 ${
          reverse ? "" : ""
        }`}
      >
        {reverse ? (
          <>
            <div className="order-2 lg:order-1">
              {imageBlock}
            </div>
            <div className="order-1 lg:order-2 px-6 md:px-12 lg:pl-12 lg:pr-[max(2rem,calc((100vw-1280px)/2))]">
              {textBlock}
            </div>
          </>
        ) : (
          <>
            <div className="px-6 md:px-12 lg:pr-12 lg:pl-[max(2rem,calc((100vw-1280px)/2))]">
              {textBlock}
            </div>
            <div>
              {imageBlock}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditorialServiceBlock;
